import { useQuery } from '@tanstack/react-query';
import type { AuthSession } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export type AdminAccessState = 'member' | 'pending' | 'unauthorized';

export interface CurrentStoreResult {
  storeId: string | null;
  storeSlug?: string | null;
  accessState?: AdminAccessState | null;
  merchantStatus?: 'pending' | 'approved' | 'rejected' | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

interface StoreSlugRow {
  slug: string;
}

interface StoreMembershipRow {
  store_id: string;
  stores: StoreSlugRow | StoreSlugRow[] | null;
}

interface MerchantProfileRow {
  status: 'pending' | 'approved' | 'rejected';
}

function isEmailOnlyAdminIdentity(session: AuthSession): boolean {
  return Boolean(session.user?.id && session.user.email && !session.user.phone);
}

export function useCurrentStore(session: AuthSession): CurrentStoreResult {
  const userId = session.user?.id ?? null;
  const isAdminIdentity = isEmailOnlyAdminIdentity(session);

  const query = useQuery({
    queryKey: ['admin', 'access', userId],
    enabled: !!userId && !session.isLoading,
    staleTime: 30_000,
    queryFn: async () => {
      if (!userId) {
        throw new Error('unreachable: enabled guard should prevent this');
      }

      if (!isAdminIdentity) {
        return {
          state: 'unauthorized' as const,
          storeId: null,
        };
      }

      const { data, error } = await supabase
        .from('store_members')
        .select('store_id, stores(slug)')
        .eq('user_id', userId)
        .is('deleted_at', null)
        .limit(1)
        .returns<StoreMembershipRow[]>();
      if (error) throw error;

      const membership = data?.[0] ?? null;
      const storeId = membership?.store_id ?? null;
      const storeRow = Array.isArray(membership?.stores)
        ? (membership?.stores[0] ?? null)
        : (membership?.stores ?? null);

      if (!storeId) {
        const { data: profile, error: profileError } = await supabase
          .from('merchant_profiles')
          .select('status')
          .eq('user_id', userId)
          .is('deleted_at', null)
          .maybeSingle<MerchantProfileRow>();
        if (profileError) throw profileError;

        const merchantStatus = profile?.status ?? 'pending';
        return {
          state: merchantStatus === 'rejected' ? ('unauthorized' as const) : ('pending' as const),
          storeId: null,
          storeSlug: null,
          merchantStatus,
        };
      }

      return {
        state: 'member' as const,
        storeId,
        storeSlug: storeRow?.slug ?? null,
        merchantStatus: null,
      };
    },
  });

  return {
    storeId: query.data?.storeId ?? null,
    storeSlug: query.data?.storeSlug ?? null,
    accessState: query.data?.state ?? null,
    merchantStatus: query.data?.merchantStatus ?? null,
    isLoading: session.isLoading || (!!session.user && (query.isLoading || query.isFetching)),
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
